import { useRef, useState } from 'react'
import { FileText, Upload, X } from 'lucide-react'
import styles from './FileDropzone.module.css'

export interface QualityReport {
  type: string
  status: 'ok' | 'warning' | 'error'
  issues: string[]
  [key: string]: unknown
}

export interface UploadedFile {
  id: string
  file: File
  qualityReport?: QualityReport | null
}

export interface FileDropzoneProps {
  files: UploadedFile[]
  onFilesAdded: (files: File[]) => void
  onFileRemoved: (id: string) => void
  accept?: string
  disabled?: boolean
}

export function FileDropzone({
  files,
  onFilesAdded,
  onFileRemoved,
  accept,
  disabled,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setIsDragging(false)
    if (disabled) return
    const dropped = Array.from(e.dataTransfer.files)
    if (dropped.length) onFilesAdded(dropped)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(e.target.files ?? [])
    if (selected.length) onFilesAdded(selected)
    e.target.value = ''
  }

  function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className={styles.root}>
      <div
        className={[
          styles.dropzone,
          isDragging ? styles.dragging : '',
          disabled ? styles.disabled : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled) setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Dateien hochladen"
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click()
        }}
      >
        <Upload size={24} className={styles.uploadIcon} />
        <p className={styles.primaryText}>
          Dateien hierher ziehen oder <span className={styles.browseLink}>auswählen</span>
        </p>
        <p className={styles.secondaryText}>PDF, CSV, XLSX, Bilder</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept ?? '.pdf,.csv,.xlsx,.xls,.jpg,.jpeg,.png'}
          onChange={handleChange}
          className={styles.hiddenInput}
          disabled={disabled}
        />
      </div>

      {files.length > 0 && (
        <ul className={styles.fileList}>
          {files.map((f) => (
            <li key={f.id} className={styles.fileItem}>
              <FileText size={16} className={styles.fileIcon} />
              <div className={styles.fileMeta}>
                <span className={styles.fileName}>{f.file.name}</span>
                <span className={styles.fileSize}>{formatSize(f.file.size)}</span>
              </div>
              {f.qualityReport && (
                <span
                  className={[
                    styles.qualityBadge,
                    styles[`quality_${f.qualityReport.status}`],
                  ].join(' ')}
                  title={f.qualityReport.issues.join('; ') || undefined}
                >
                  {f.qualityReport.status === 'ok'
                    ? 'OK'
                    : f.qualityReport.status === 'warning'
                      ? 'Hinweis'
                      : 'Fehler'}
                </span>
              )}
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => onFileRemoved(f.id)}
                aria-label={`${f.file.name} entfernen`}
                disabled={disabled}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
